import {
  CODEC_VERSION,
  LINE_BITS,
  RHYME_BITS,
  TOTAL_LINES,
  WITHIN_CLASS_BITS,
  rhymeClassOf,
  withinClassIndexOf,
} from "../codec/constants";
import { serviceWorkerRegistration } from "../service-worker";
import { AmbiguousCorpusLineError, type Corpus, type CorpusManifest, type CorpusManifestClass, type InstallProgress } from "./types";
import { DECODE_NORMALIZER, normalizeCorpusLine } from "./normalize";

const DB_NAME = "poem-codec-corpora";
const DB_VERSION = 2;
const LINE_STORE = "reverse-buckets";
const META_STORE = "meta";
const BUCKET_COUNT = 256;

function lineHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains("lines")) database.deleteObjectStore("lines");
      if (database.objectStoreNames.contains(LINE_STORE)) database.deleteObjectStore(LINE_STORE);
      database.createObjectStore(LINE_STORE);
      if (!database.objectStoreNames.contains(META_STORE)) database.createObjectStore(META_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open the corpus index."));
  });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction was aborted."));
  });
}

async function sha256Hex(value: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", value);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function manifestFingerprint(manifest: CorpusManifest): string {
  return `reverse-v3:${manifest.classes.map((entry) => entry.sha256).join(":")}`;
}

function parseShard(text: string, expected: number): string[] {
  const lines = text.endsWith("\n") ? text.slice(0, -1).split("\n") : text.split("\n");
  if (lines.length !== expected || lines.some((line) => line.length === 0)) {
    throw new Error(`Corpus shard contains ${lines.length.toLocaleString()} lines; expected ${expected.toLocaleString()}.`);
  }
  return lines;
}

export async function resolveCorpusLineIndex(
  line: string,
  candidatesForAlias: (alias: string) => Promise<readonly number[]>,
  getLine: (index: number) => Promise<string>,
): Promise<number | undefined> {
  for (const index of await candidatesForAlias(line)) {
    if ((await getLine(index)) === line) return index;
  }

  const normalized = normalizeCorpusLine(line);
  const matches = new Set<number>();
  for (const index of await candidatesForAlias(normalized)) {
    if (normalizeCorpusLine(await getLine(index)) === normalized) matches.add(index);
  }
  if (matches.size > 1) throw new AmbiguousCorpusLineError();
  return matches.values().next().value as number | undefined;
}

export class BrowserCorpus implements Corpus {
  readonly id: string;
  private readonly baseUrl: URL;
  private readonly classCache = new Map<number, string[]>();
  private databasePromise?: Promise<IDBDatabase>;
  private readyPromise?: Promise<void>;

  private constructor(readonly manifest: CorpusManifest, manifestUrl: URL) {
    this.id = manifest.corpusId;
    this.baseUrl = new URL("./", manifestUrl);
  }

  static async load(manifestPath = "./corpus/cn-1/manifest.json"): Promise<BrowserCorpus> {
    const manifestUrl = new URL(manifestPath, window.location.href);
    const response = await fetch(manifestUrl);
    if (!response.ok) throw new Error(`Corpus manifest unavailable (${response.status}).`);
    const manifest = (await response.json()) as CorpusManifest;
    BrowserCorpus.validateManifest(manifest);
    return new BrowserCorpus(manifest, manifestUrl);
  }

  private static validateManifest(manifest: CorpusManifest): void {
    if (
      manifest.format !== 1 ||
      manifest.codecVersion !== CODEC_VERSION ||
      manifest.lineBits !== LINE_BITS ||
      manifest.rhymeBits !== RHYME_BITS ||
      manifest.withinClassBits !== WITHIN_CLASS_BITS ||
      manifest.totalLines !== TOTAL_LINES ||
      manifest.classes.length !== 2 ** RHYME_BITS ||
      manifest.decodeNormalizer?.library !== DECODE_NORMALIZER.library ||
      manifest.decodeNormalizer.version !== DECODE_NORMALIZER.version ||
      manifest.decodeNormalizer.configuration !== DECODE_NORMALIZER.configuration ||
      manifest.decodeNormalizer.pipeline !== DECODE_NORMALIZER.pipeline ||
      manifest.decodeNormalizer.dictionarySource !== DECODE_NORMALIZER.dictionarySource ||
      manifest.decodeNormalizer.phraseDictionarySha256 !== DECODE_NORMALIZER.phraseDictionarySha256 ||
      manifest.decodeNormalizer.characterDictionarySha256 !== DECODE_NORMALIZER.characterDictionarySha256 ||
      manifest.decodeNormalizer.canonicalExceptionsSha256 !== DECODE_NORMALIZER.canonicalExceptionsSha256 ||
      manifest.decodeNormalizer.integrity !== DECODE_NORMALIZER.integrity
    ) {
      throw new Error("Corpus manifest is incompatible with codec v1.");
    }
  }

  private database(): Promise<IDBDatabase> {
    this.databasePromise ??= openDatabase();
    return this.databasePromise;
  }

  private async loadClass(entry: CorpusManifestClass, verify = false): Promise<string[]> {
    const cached = this.classCache.get(entry.id);
    if (cached) return cached;
    const url = new URL(entry.lines, this.baseUrl);
    const response = ("caches" in window ? await caches.match(url, { ignoreVary: true }) : undefined) ?? (await fetch(url));
    if (!response.ok) throw new Error(`Unable to download corpus shard ${entry.id + 1}.`);
    const buffer = await response.arrayBuffer();
    if (verify && (await sha256Hex(buffer)) !== entry.sha256) {
      throw new Error(`Checksum mismatch in corpus shard ${entry.id + 1}.`);
    }
    const lines = parseShard(new TextDecoder().decode(buffer), entry.count);
    this.classCache.set(entry.id, lines);
    return lines;
  }

  async ensureReady(onProgress?: (progress: InstallProgress) => void): Promise<void> {
    this.readyPromise ??= this.install(onProgress);
    return this.readyPromise;
  }

  private async install(onProgress?: (progress: InstallProgress) => void): Promise<void> {
    const database = await this.database();
    const fingerprint = manifestFingerprint(this.manifest);
    await this.cacheCorpus(onProgress);
    const current = await requestValue(
      database.transaction(META_STORE, "readonly").objectStore(META_STORE).get(this.id),
    );
    if (current === fingerprint) {
      onProgress?.({ phase: "ready", complete: this.manifest.totalLines, total: this.manifest.totalLines });
      return;
    }

    let indexed = 0;
    const buckets = Array.from({ length: BUCKET_COUNT }, () => [] as number[]);
    for (const entry of this.manifest.classes) {
      onProgress?.({ phase: "download", complete: entry.id, total: this.manifest.classes.length });
      const lines = await this.loadClass(entry, true);
      for (let within = 0; within < lines.length; within += 1) {
        const line = lines[within]!;
        const index = entry.id * 2 ** WITHIN_CLASS_BITS + within;
        for (const alias of new Set([line, normalizeCorpusLine(line)])) {
          const hash = lineHash(alias);
          buckets[hash >>> 24]!.push(hash, index);
        }
      }
      indexed += lines.length;
      onProgress?.({ phase: "index", complete: indexed, total: this.manifest.totalLines });
    }

    const indexTransaction = database.transaction(LINE_STORE, "readwrite", { durability: "relaxed" });
    const indexStore = indexTransaction.objectStore(LINE_STORE);
    for (let bucket = 0; bucket < buckets.length; bucket += 1) {
      const compact = Uint32Array.from(buckets[bucket]!);
      indexStore.put(compact.buffer, `${this.id}\0${bucket}`);
    }
    await transactionDone(indexTransaction);
    this.classCache.clear();
    const metaTransaction = database.transaction(META_STORE, "readwrite");
    metaTransaction.objectStore(META_STORE).put(fingerprint, this.id);
    await transactionDone(metaTransaction);
    onProgress?.({ phase: "ready", complete: indexed, total: this.manifest.totalLines });
  }

  private async cacheCorpus(onProgress?: (progress: InstallProgress) => void): Promise<void> {
    const registration = await serviceWorkerRegistration();
    if (!registration) return;
    const worker = registration.active;
    if (!worker) return;
    const urls = this.manifest.classes.map((entry) => new URL(entry.lines, this.baseUrl).href);
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        navigator.serviceWorker.removeEventListener("message", receive);
        reject(new Error("Corpus caching timed out."));
      }, 120_000);
      const receive = (event: MessageEvent<{ type?: string; complete?: number; total?: number; message?: string }>) => {
        if (event.data.type === "CACHE_CORPUS_PROGRESS") {
          onProgress?.({
            phase: "download",
            complete: event.data.complete ?? 0,
            total: event.data.total ?? urls.length,
          });
        }
        if (event.data.type === "CACHE_CORPUS_DONE") {
          window.clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener("message", receive);
          resolve();
        }
        if (event.data.type === "CACHE_CORPUS_ERROR") {
          window.clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener("message", receive);
          reject(new Error(event.data.message ?? "Corpus caching failed."));
        }
      };
      navigator.serviceWorker.addEventListener("message", receive);
      worker.postMessage({ type: "CACHE_CORPUS", urls });
    });
  }

  async getLine(index: number): Promise<string> {
    if (!Number.isInteger(index) || index < 0 || index >= this.manifest.totalLines) {
      throw new RangeError(`Corpus index ${index} is out of range.`);
    }
    const classId = rhymeClassOf(index);
    const entry = this.manifest.classes[classId];
    if (!entry) throw new Error(`Corpus rhyme class ${classId} is missing.`);
    const lines = await this.loadClass(entry);
    const line = lines[withinClassIndexOf(index)];
    if (line === undefined) throw new Error(`Corpus line ${index} is missing.`);
    return line;
  }

  async getIndex(line: string): Promise<number | undefined> {
    const database = await this.database();
    const candidatesForAlias = async (alias: string): Promise<number[]> => {
      const hash = lineHash(alias);
      const buffer = await requestValue<ArrayBuffer | undefined>(
        database
          .transaction(LINE_STORE, "readonly")
          .objectStore(LINE_STORE)
          .get(`${this.id}\0${hash >>> 24}`),
      );
      if (!buffer) return [];
      const entries = new Uint32Array(buffer);
      const candidates: number[] = [];
      for (let offset = 0; offset < entries.length; offset += 2) {
        if (entries[offset] === hash) candidates.push(entries[offset + 1]!);
      }
      return candidates;
    };
    return resolveCorpusLineIndex(line, candidatesForAlias, (index) => this.getLine(index));
  }
}
